import type { MessageKey } from "@/i18n";
import { useI18n } from "@/i18n";
import { cx } from "@/lib/cx";

type Tone = "accent" | "success" | "danger" | "warning" | "info" | "muted" | "solid";

const toneClass: Record<Tone, string> = {
  accent: "text-accent-text bg-[color-mix(in_oklab,var(--c-accent)_12%,transparent)]",
  success: "text-success bg-[color-mix(in_oklab,var(--c-success)_13%,transparent)]",
  danger: "text-danger bg-[color-mix(in_oklab,var(--c-danger)_12%,transparent)]",
  warning: "text-warning bg-[color-mix(in_oklab,var(--c-warning)_15%,transparent)]",
  info: "text-info bg-[color-mix(in_oklab,var(--c-info)_12%,transparent)]",
  muted: "text-muted bg-course",
  solid: "text-surface bg-text",
};

const STATUS_TONES: Record<string, Tone> = {
  draft: "info",
  approved: "success",
  rejected: "danger",
  published: "solid",
  queued: "muted",
  sent: "success",
  failed: "danger",
  manual: "warning",
  running: "info",
  succeeded: "success",
  empty: "muted",
  skipped: "warning",
  pending_code: "warning",
  pending_password: "warning",
  active: "success",
  revoked: "danger",
  critical: "danger",
  warning: "warning",
  info: "info",
  flagged: "warning",
};

export function Chip({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span
      className={cx(
        "inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-xs font-medium whitespace-nowrap",
        toneClass[tone],
      )}
    >
      {children}
    </span>
  );
}

export function StatusChip({ status }: { status: string }) {
  const { t, has } = useI18n();
  const key = `status.${status}`;
  return (
    <Chip tone={STATUS_TONES[status] ?? "muted"}>{has(key) ? t(key as MessageKey) : status}</Chip>
  );
}
